<template>
  <div v-if="hasUserRole">
    <gf-custom-tooltip
      width="200px"
      class="d-inline-block"
      position="left"
    >
      <template #element>
        <gf-dropdown
          position="right"
          btn-class="btn btn-sm btn-success"
          :btn-text="$t('file.addFiles')"
          :disabled="!hasEditPermissions"
        >
          <a
            v-if="!isNewRepo"
            class="dropdown-item"
            :href="`/project/${alias}/${projectAlias}/edit/create-blob?branch=${branchName}`"
          >
            <i class="gf-icon gf-icon-plus mr-1"></i>
            {{ $t('file.createFile') }}
          </a>

          <button
            v-else
            class="dropdown-item"
            type="button"
            @click="openAddFilesModal"
          >
            <i class="gf-icon gf-icon-plus mr-1"></i>
            {{ $t('file.createFile') }}
          </button>

          <button
            class="dropdown-item"
            type="button"
            @click="openUploadFilesModal"
          >
            <i class="gf-icon gf-icon-upload mr-1"></i>
            {{ $t('file.uploadFiles') }}
          </button>
        </gf-dropdown>
      </template>
      <template
        v-if="!hasEditPermissions"
        #content
      >
        <small>{{ cannotEditText }}</small>
      </template>
    </gf-custom-tooltip>
    <gf-custom-modal-window
      v-if="hasEditPermissions"
      ref="gfCustomModalWindowUploadFiles"
      gf-modal-data-name="uploadFilesModal"
      :gf-modal-header="$t('file.uploadFiles')"
      gf-modal-wrapper-width="500px"
      @close="resetUploadErrors"
    >
      <template #body>
        <project-upload-files-form
          ref="uploadFilesForm"
          :is-new-repo="isNewRepo"
          :branch="branchName"
          @cancel="closeUploadFilesModal"
          @submit="uploadFiles"
        />
      </template>
    </gf-custom-modal-window>

    <gf-custom-modal-window
      v-if="isNewRepo"
      ref="gfCustomModalWindowCreateFiles"
      gf-modal-data-name="createFilesModal"
      :gf-modal-header="$t('file.createBranch')"
      gf-modal-wrapper-width="500px"
    >
      <template #body>
        <div class="w-100">
          <b>{{ $t('commit.inNewBranch') }}</b>
          <br />
          <label
            class="gf-counted-input"
            for="newRepoBranchName"
            :data-current-length="newRepoBranchName?.length + ' / 250'"
          >
            <input
              v-model="newRepoBranchName"
              type="text"
              class="form-control form-control-sm w-100 mt-2"
              id="newRepoBranchName"
              minlength="1"
              maxlength="250"
              required
              :placeholder="$t('message.branch_name')"
            />
          </label>

          <div class="d-flex align-items-center justify-content-end">
            <a
              class="btn btn-sm btn-success"
              :href="`/project/${alias}/${projectAlias}/edit/create-blob?branch=${newRepoBranchName}${
                isNewRepo ? `&isNewRepo=${isNewRepo}` : ''
              }`"
              :class="{ disabled: !isNewRepoAddFilesLinkDisabled }"
            >
              Создать
            </a>
          </div>
        </div>
      </template>
    </gf-custom-modal-window>
  </div>
</template>

<script>
import GfCustomTooltip from '@/shared-modules/shared-components/shared/GfCustomTooltip';
import GfDropdown from '@/shared-modules/shared-components/shared/GfDropdown';
import RepoS from '@shared/services/repository.service';
import projectFilesMessages from '@/shared-modules/modules/project-files/project-files.messages';
import GfCustomModalWindow from '@shared/shared-components/shared/GfCustomModalWindow';
import { messageNotification } from '@shared/services/gf.notification.service';
import ProjectUploadFilesForm from '@/shared-modules/modules/project-files/components/project-upload-files-form';
import qs from 'qs';
import { FORM_NAME_ENUMS } from '@/shared-modules/variables/form-name.enums';

export default {
  name: 'GfAddFilesFropdown',
  components: { GfCustomTooltip, GfDropdown, GfCustomModalWindow, ProjectUploadFilesForm },
  data() {
    return {
      newRepoBranchName: ''
    };
  },
  props: {
    alias: {
      type: String,
      default: ''
    },
    projectAlias: {
      type: String,
      default: ''
    },
    branchName: {
      type: String,
      default: ''
    },
    lastCommitHash: {
      type: String,
      default: ''
    },
    isNewRepo: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    branchPermissions() {
      if (!this.isNewRepo) {
        return (
          this.$store.getters.getBranchPermissionByBranch(this.branchName) ||
          this.getBranchPermissions()
        );
      } else return true;
    },
    hasEditPermissions() {
      return this.isNewRepo ? true : this.branchPermissions?.ALLOW_TO_PUSH;
    },
    cannotEditText() {
      return this.$t('file.notEnoughPermissionsToAddFiles');
    },
    hasUserRole() {
      return this.isNewRepo ? true : this.branchPermissions?.IS_USER_IN_PROJECT || false;
    },
    isNewRepoAddFilesLinkDisabled() {
      return this.newRepoBranchName?.length > 0;
    }
  },
  methods: {
    getBranchPermissions() {
      RepoS.getBlobAndBranchPermissions(this.alias, this.projectAlias, {
        file: '',
        branch: this.branchName,
        commit: this.lastCommitHash
      })
        .then((res) => res)
        .catch((err) => {
          console.error(err);
        });
    },
    openUploadFilesModal() {
      this.$refs.gfCustomModalWindowUploadFiles.openModalWindow();
    },
    closeUploadFilesModal() {
      this.$refs.gfCustomModalWindowUploadFiles.closeModalWindow();
      this.resetUploadErrors();
    },
    openAddFilesModal() {
      this.$refs.gfCustomModalWindowCreateFiles.openModalWindow();
    },
    closeAddFilesModal() {
      this.$refs.gfCustomModalWindowCreateFiles.closeModalWindow();
      this.newRepoBranchName = null;
    },
    resetUploadErrors() {
      this.$refs.uploadFilesForm.resetFiles();
      this.$store.dispatch('setFormErrors', {
        formName: FORM_NAME_ENUMS.UPLOAD_FILES_FORM,
        errorResponse: null
      });
    },
    uploadFiles(data) {
      if (!data?.filesData?.isMaxSizeExceeded) {
        const formData = new FormData();
        data?.filesData?.files?.forEach((file) => {
          formData.append('files', file);
        });
        formData.append('commitHash', this.lastCommitHash);
        formData.append('sourceBranch', this.branchName);
        formData.append('targetBranch', data.isDirectCommit ? this.branchName : data.newBranchName);
        formData.append('commitMessage', data.message);
        formData.append('filesPath', '');
        formData.append('projectAlias', this.projectAlias);
        formData.append('ownerAlias', this.alias);
        RepoS.uploadFiles(this.alias, this.projectAlias, formData)
          .then((newData) => {
            const encodedParams = qs
              .stringify({
                file: newData?.filesPath,
                branch: newData?.targetBranch
              })
              .replace(/%25/g, '%');
            window.location.href = `/project/${this.alias}/${this.projectAlias}/file?${encodedParams}`;
          })
          .catch((err) => {
            if (err?.response?.data?.status === 403) {
              this.closeUploadFilesModal();
              messageNotification('danger', {
                message: err.response.data.detail,
                hasTimer: false
              });
            }
          });
      }
    }
  },
  i18n: { messages: projectFilesMessages }
};
</script>

<style lang="scss"></style>
